import Dot from 'components/Dot/Dot';

import {
  RETAIL_RATE_REQUEST_STATUS as STATUS,
  RETAIL_RATE_REQUEST_STATUS_REASON as STATUS_REASON,
  getRetailRateRequestStatus,
  getRetailRateRequestStatusReason,
} from '../ServicePricingSection/pricingStatusStepper.helpers';

import { messages } from './PricingStatusStepper.messages';

// Visual state of a single timeline step.
export const STEP_STATE = {
  DONE: 'done',
  CURRENT: 'current',
  FAILED: 'failed',
  UPCOMING: 'upcoming',
};

const REJECTION_REASON_MESSAGE = {
  [STATUS_REASON.PROOF_UNTRUSTED]: messages.reasonProofUntrusted,
  [STATUS_REASON.PARTNER_DECLINED]: messages.reasonPartnerDeclined,
  [STATUS_REASON.GENRE_DISALLOWED]: messages.reasonGenreDisallowed,
  [STATUS_REASON.PARTNER_DISAGREEMENT]: messages.reasonPartnerDisagreement,
};

const rejectionReasonMessage = statusReason => REJECTION_REASON_MESSAGE[statusReason] || messages.reasonGeneric;

// A request is "rejected" (action required) — drives the read-only-unless-rejected
// gating on the edit pages and the failed timeline step.
export const isRejected = retailRateRequest => getRetailRateRequestStatus(retailRateRequest) === STATUS.REJECTED;

const isApproved = retailRateRequest => getRetailRateRequestStatus(retailRateRequest) === STATUS.APPROVED;

// PENDING and REQUESTED both mean "submitted, awaiting verification". (Unlike the
// list-page helper, MANUAL_HANDOFF is NOT pending here — the stepper treats it as
// a terminal state with no timeline, keeping the edit page editable.)
const isPending = retailRateRequest => {
  const status = getRetailRateRequestStatus(retailRateRequest);
  return status === STATUS.PENDING || status === STATUS.REQUESTED;
};

// Maps a RetailRateRequest to the view model the stepper renders: the three
// timeline steps plus a compact status label/colour. Returns null when there's
// no request to display (caller renders nothing).
export const getPricingStatusView = retailRateRequest => {
  if (!retailRateRequest) return null;

  const submittedStep = { titleMessage: messages.stepSubmittedTitle, state: STEP_STATE.DONE };

  if (isRejected(retailRateRequest)) {
    return {
      isRejected: true,
      statusLabel: messages.statusVerificationFailed,
      statusColor: Dot.COLORS.RED,
      steps: [
        submittedStep,
        { titleMessage: messages.stepVerificationTitle, state: STEP_STATE.DONE },
        {
          titleMessage: messages.stepFailedTitle,
          subtitleMessage: rejectionReasonMessage(getRetailRateRequestStatusReason(retailRateRequest)),
          state: STEP_STATE.FAILED,
        },
      ],
    };
  }

  if (isApproved(retailRateRequest)) {
    const rateChange = getRetailRateRequestStatusReason(retailRateRequest) === STATUS_REASON.RATE_CHANGES_NEEDED;
    return {
      isRejected: false,
      statusLabel: rateChange ? messages.statusAvailableWithChanges : messages.statusAvailable,
      statusColor: Dot.COLORS.GREEN,
      steps: [
        submittedStep,
        { titleMessage: messages.stepVerificationTitle, state: STEP_STATE.DONE },
        {
          titleMessage: messages.stepApprovedTitle,
          subtitleMessage: rateChange ? messages.stepApprovedRateChangeSubtitle : messages.stepApprovedSubtitle,
          state: STEP_STATE.DONE,
        },
      ],
    };
  }

  // Default to the pending view for PENDING/REQUESTED (and any unspecified
  // in-flight status) — the request exists but isn't approved or rejected.
  if (isPending(retailRateRequest)) {
    return {
      isRejected: false,
      statusLabel: messages.statusPendingReview,
      statusColor: Dot.COLORS.YELLOW,
      steps: [
        submittedStep,
        {
          titleMessage: messages.stepVerificationTitle,
          subtitleMessage: messages.stepVerificationSubtitle,
          state: STEP_STATE.CURRENT,
        },
        { titleMessage: messages.stepApprovedTitle, state: STEP_STATE.UPCOMING },
      ],
    };
  }

  // No timeline for terminal/unknown states (CANCELLED, MANUAL_HANDOFF,
  // UNSPECIFIED). The caller treats these as "no request" — editable — so a
  // partner is never stuck read-only with no path to act.
  return null;
};

// Whether a request should make the pricing inputs read-only. True only when
// the stepper shows a non-rejected status (pending/approved). Rejected stays
// editable (action required), and any status the stepper can't render
// (cancelled/manual-handoff/unspecified) falls back to editable too — keeping
// the read-only gate and the stepper visibility in lockstep so the two can
// never disagree and trap the partner. Pages compute editability via this.
export const blocksEditing = retailRateRequest => {
  const view = getPricingStatusView(retailRateRequest);
  return !!view && !view.isRejected;
};
